import { Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

/**
 * Single source of truth for viewport breakpoints, built on the CDK
 * {@link BreakpointObserver}. Replaces the ad-hoc `window.innerWidth < 768`
 * resize logic that used to live in `sidebar.component.ts` and push into
 * `ThemeService.isSmallDisplay$`.
 *
 * Thresholds are expressed as `max-width` media queries (the `.98px` upper bound
 * is the conventional CDK/Bootstrap technique to avoid a 1px dead-zone between
 * adjacent breakpoints):
 *  - {@link SMALL_DISPLAY_QUERY} `(max-width: 767.98px)` — parity with the legacy
 *    768px boundary; drives sidebar collapse and (incoming) the mobile shell.
 *  - {@link HANDSET_QUERY} phones; {@link TABLET_QUERY} the 600–1023px band.
 */
@Injectable({ providedIn: 'root' })
export class BreakpointService {
    /** Parity with the legacy `window.innerWidth < 768` small-display threshold. */
    public static readonly SMALL_DISPLAY_QUERY = '(max-width: 767.98px)';
    public static readonly HANDSET_QUERY = '(max-width: 599.98px)';
    public static readonly TABLET_QUERY = '(min-width: 600px) and (max-width: 1023.98px)';

    /** True when the viewport is below the 768px small-display threshold. */
    public readonly isSmallDisplay$: Observable<boolean>;
    /** True on phone-sized viewports (≤ 599.98px). */
    public readonly isHandset$: Observable<boolean>;
    /** True on tablet-sized viewports (600–1023.98px). */
    public readonly isTablet$: Observable<boolean>;

    constructor(private breakpointObserver: BreakpointObserver) {
        this.isSmallDisplay$ = this.observeMatches(BreakpointService.SMALL_DISPLAY_QUERY);
        this.isHandset$ = this.observeMatches(BreakpointService.HANDSET_QUERY);
        this.isTablet$ = this.observeMatches(BreakpointService.TABLET_QUERY);
    }

    private observeMatches(query: string): Observable<boolean> {
        return this.breakpointObserver.observe(query).pipe(
            map((result) => result.matches),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }
}
